import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Mic, Heart, ArrowRight, Send, CheckCircle, ShieldCheck, Globe, Mountain, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { PageTransition } from '../components/PageTransition';
import { Season2Announcement } from '../components/Season2Announcement';
import { HelpUsReach } from '../components/HelpUsReach';
import { latestSeason2Episode, season2Premiere } from '../data/season2Episodes';
export function HomePage() {
  const [formResult, setFormResult] = useState('');
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!captchaChecked) {
      setFormResult('Please verify you are not a robot.');
      return;
    }
    setFormResult('Sending....');
    const formData = new FormData(event.currentTarget);
    formData.append('access_key', '7ab87c60-088d-4fc0-91c1-42d023f2a608');
    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (data.success) {
      setFormResult('Form Submitted Successfully');
      event.currentTarget.reset();
    } else {
      setFormResult('Error');
    }
  };
  return <PageTransition data-id="element-409" data-mp="1rrtwz6aigxp5">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden pt-28" data-id="element-410" data-mp="1vr2ridddkzlr">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-411" data-mp="20p4ayzepr0xr">
          <img src="https://cdn.magicpatterns.com/uploads/4XsQyzBu4UT6zjnQT4ucNY/Person_praying_and_kneeling_with_arms_raised.webp" alt="" className="w-full h-full object-cover" data-id="element-412" data-mp="25pikklvyqixw" />
          <div className="absolute inset-0 bg-dark-900/65" data-id="element-413" data-mp="3h3w2la41c460"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center" data-id="element-414" data-mp="2rga65wna9i7u">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="mb-6 mt-8 flex justify-center" data-id="element-415" data-mp="2riminua9v0di">
            <img src="https://cdn.magicpatterns.com/uploads/ue8vBFZwEq6iFGtuLhrkM6/Podcastlogo3doves.jpg" alt="3 Doves Podcast Logo" className="w-32 h-32 md:w-48 md:h-48 rounded-full border-4 border-gold-500/20 shadow-2xl shadow-gold-500/10" data-id="element-416" data-mp="28pircfkj0zm2" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-417" data-mp="2don0553jgvbv">
            Honest Faith. <span className="text-gold-500" data-id="element-418" data-mp="5bi32qvsxx29">Real Stories.</span>
            <br data-id="element-419" data-mp="3mqy9au9i2pwj" />
            Lives Transformed.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-420" data-mp="3roqbqi7rtuez">
            Join Jason Pawloski for weekly conversations about walking with God
            in the middle of everyday life. No polish, just truth.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.8
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-421" data-mp="2v0tudb4iev2p">
            <a href="#listen-platforms" onClick={e => {
            e.preventDefault();
            document.getElementById('listen-platforms')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} data-id="element-422" data-mp="35i729m92mloj">
              <Button size="lg" leftIcon={<Play className="w-5 h-5 fill-current" data-id="element-424" data-mp="3o7vlwj7h2fol" />} data-id="element-423" data-mp="3t8jjjnq7hurs">
                Listen Now
              </Button>
            </a>
            <Link to="/guests" data-id="element-425" data-mp="3k5s83nxbyz05">
              <Button variant="outline" size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-427" data-mp="2cqvs1j5h7ssg" />} data-id="element-426" data-mp="3k8mu3jvzsrvo">
                Meet Our Guests
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newest Season 2 Release */}
      <Season2Announcement episode={latestSeason2Episode} data-id="element-428" data-mp="saoa76ql3ias" />

      {/* Help 3 Doves Reach Millions */}
      <HelpUsReach data-id="element-429" data-mp="xaanfg6ewyjl" />

      {/* Featured Episode */}
      <section className="py-20 bg-dark-800 relative border-t border-dark-600" data-id="element-430" data-mp="x8pt9ro0fhm9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-431" data-mp="121iyshraytze">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-432" data-mp="1703vw8b9cxjm">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-433" data-mp="1726l4q4l6mna">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-dark-600 border border-dark-500 group" data-id="element-434" data-mp="3hsol2biwq5qr">
                <img src={season2Premiere.image} alt={season2Premiere.imageAlt} className="w-full h-full object-cover" data-id="element-435" data-mp="3mri1octhqcv5" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors duration-300" data-id="element-436" data-mp="oyq2h06sfpgr">
                  <a href={season2Premiere.spotifyUrl} target="_blank" rel="noopener noreferrer" aria-label={`Listen to ${season2Premiere.title}`} className="w-16 h-16 rounded-full bg-gold-500 flex items-center justify-center text-dark-900 shadow-lg shadow-gold-500/30 group-hover:scale-110 transition-transform duration-300 cursor-pointer" data-id="element-437" data-mp="xy379fjyvpv5">
                    <Play className="w-7 h-7 fill-current ml-1" data-id="element-438" data-mp="1hmvpjtlbdqjr" />
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-439" data-mp="1qnacexdxfdpd">
              <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-440" data-mp="209uqqvwbs7z7">
                Previous Episode
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-441" data-mp="1rhj29fkruulv">
                {season2Premiere.title} — {season2Premiere.guest}'s Story
              </h2>
              <p className="text-white/80 text-lg mb-6 leading-relaxed" data-id="element-442" data-mp="1hjwiofsj2evk">
                {season2Premiere.description[0]}
              </p>
              <p className="text-white/80 text-lg mb-8 leading-relaxed" data-id="element-443" data-mp="1mk10qa933hnw">
                {season2Premiere.description[1]}
              </p>
              <div className="flex items-center gap-4 text-sm text-white/60 mb-8" data-id="element-444" data-mp="18lkm7x4zcmv7">
                <span className="flex items-center" data-id="element-445" data-mp="1ck0fujvkgthl">
                  <Mic className="w-4 h-4 mr-2 text-gold-500" data-id="element-446" data-mp="1t0kqy9qsqppa" /> Season 2 ·
                  Episode 1
                </span>
                <span className="w-1 h-1 rounded-full bg-dark-400" data-id="element-447" data-mp="1o2j7votdeqdt"></span>
                <span data-id="element-448" data-mp="1j1c0vrz1xv87">with {season2Premiere.guest}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-449" data-mp="1e321f56ao8gs">
                <a href={season2Premiere.spotifyUrl} target="_blank" rel="noopener noreferrer" data-id="element-450" data-mp="1e2sejqgjqhfb">
                  <Button leftIcon={<Play className="w-4 h-4 fill-current" data-id="element-452" data-mp="1595kq5vwfo8r" />} data-id="element-451" data-mp="159p31npgoenb">
                    Listen Now
                  </Button>
                </a>
                <Link to="/podcasts/season-2" data-id="element-453" data-mp="vckexic2293v">
                  <Button variant="outline" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-455" data-mp="qdzbzb5f5bbq" />} data-id="element-454" data-mp="vdvhxfxofctx">
                    Read Jim's Full Story
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sponsors */}
      <section className="py-20 bg-dark-800 border-t border-dark-600" data-id="element-456" data-mp="320t4mzcu5yxu">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-457" data-mp="31yzxzk283epu">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-458" data-mp="370xmasphczpn">
            <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-459" data-mp="2jb38a7tfd53t">
              Made Possible By
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-460" data-mp="2szuyaaexdfc3">
              Our Sponsors
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-461" data-mp="1vjpjgbuhfrnl"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-462" data-mp="20isdz2cu6fhb">
            {[{
            name: "Horseman's Landscape",
            url: 'https://horsemanslandscape.com',
            tagline: 'Quality Landscaping Services'
          }, {
            name: 'First Response Water & Mold Removal',
            url: 'https://firstresponsewaterandmold.com/',
            tagline: 'Disaster Restoration Experts'
          }, {
            name: 'Chill of Cape Coral',
            url: 'https://www.capechill.net/',
            tagline: 'Cape Coral, Florida'
          }].map((sponsor, index) => <motion.a key={sponsor.name} href={sponsor.url} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group relative overflow-hidden rounded-xl border border-dark-500 bg-dark-700 p-8 text-center hover:border-gold-500/50 transition-all duration-300" data-id="element-463" data-mp="22xkgs1uq1owg" data-mpi={index}>
                <div className="absolute inset-0 bg-gold-500/0 group-hover:bg-gold-500/5 transition-colors duration-300" data-id="element-464" data-mp="3elcr2s1nbx7w"></div>
                <div className="relative z-10" data-id="element-465" data-mp="34tgp35wfvmye">
                  <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-dark-600 border border-dark-400 flex items-center justify-center group-hover:border-gold-500/50 transition-colors duration-300" data-id="element-466" data-mp="34snkobkyd9jf">
                    <Heart className="w-6 h-6 text-gold-500" data-id="element-467" data-mp="2vubto8g6c9qg" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-2 group-hover:text-gold-400 transition-colors duration-300" data-id="element-468" data-mp="2qsmjsw4ou23y">
                    {sponsor.name}
                  </h3>
                  <p className="text-white/50 text-sm mb-4" data-id="element-469" data-mp="2c5xicoqvleux">
                    {sponsor.tagline}
                  </p>
                  <span className="inline-flex items-center text-sm text-gold-500 gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-470" data-mp="2h7mvqb1km4m6">
                    Visit Website
                    <ArrowRight className="w-3.5 h-3.5" data-id="element-471" data-mp="278e61udqnc5m" />
                  </span>
                </div>
              </motion.a>)}
          </div>
        </div>
      </section>

      {/* Outreach Programs */}
      <section className="py-24 bg-dark-700" data-id="element-472" data-mp="2zvrypgaelljx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473" data-mp="2m5ogd0enabzr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-474" data-mp="2m3byen8w01mp">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-475" data-mp="177sjmxqprsce">
              Outreach Programs We Support
            </h2>
            <p className="text-white/80 text-lg max-w-3xl mx-auto" data-id="element-476" data-mp="1287h6dcc51ux">
              Faith without action is incomplete. We partner with organizations
              making a real difference in the lives of those who need it most.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-477" data-mp="o8odpxugyeoy">
            {/* Operation Olive Branch */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} data-id="element-478" data-mp="jbeqmoujyf4a">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-479" data-mp="24nzep0vdkxzm">
                <div className="mb-6" data-id="element-480" data-mp="24psouq5760cd">
                  <img src="https://cdn.magicpatterns.com/uploads/mmGRaZje5gHPYZ1hRf9zTx/operationolivebranch.jpg" alt="Operation Olive Branch" className="w-48 h-auto mx-auto" data-id="element-481" data-mp="e742cjk240ow" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-482" data-mp="s5vjkeu7u2h9">
                  Operation Olive Branch
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-483" data-mp="x4yakvdd2868">
                  Serving the Least of These
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-484" data-mp="16urkzt1roh8w">
                  Our Essential Bags program provides homeless individuals with
                  dignity and basic necessities — because every person is made
                  in the image of God. Each bag includes toiletries, hygiene
                  products, water, and snacks to meet immediate needs with
                  compassion.
                </p>
                <Link to="/outreach" data-id="element-485" data-mp="1v0aqf95g9qsd">
                  <Button variant="outline" className="w-full" data-id="element-486" data-mp="1pzmnbp3y5jhs">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* The Million Movement */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} data-id="element-487" data-mp="1gzq6g9nstoxa">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-488" data-mp="1zxso23e0ojl2">
                <div className="mb-6 flex justify-center" data-id="element-489" data-mp="1zvq15s85v6kk">
                  <Globe className="w-24 h-24 text-gold-500" data-id="element-490" data-mp="2wgqm5ff83pai" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-491" data-mp="2rognnb01lgwm">
                  The Million Movement
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-492" data-mp="ps825ymd7651">
                  Child Sponsorship
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-493" data-mp="g2etp6hir750">
                  Founded by Child Advocate Jeremy Willet, The Million Movement
                  produces award-winning campaigns that travel on nationwide
                  concert tours to find sponsors for children living in poverty.
                  Their goal: one million children sponsored by 2050.
                </p>
                <Link to="/million-movement" data-id="element-494" data-mp="3t8om3100vw6b">
                  <Button variant="outline" className="w-full" data-id="element-495" data-mp="3t9zpgzpk31ww">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* Ends Adventures */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-496" data-mp="1wfwlonkcau4i">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-497" data-mp="3qp1t1q2n5tua">
                <div className="mb-6 flex justify-center" data-id="element-498" data-mp="3lvz4jwcq1aky">
                  <Mountain className="w-24 h-24 text-gold-500" data-id="element-499" data-mp="3j14lpfxihqs" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-500" data-mp="3kvpj3r7a9uv">
                  Ends of the Earth Adventures
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-501" data-mp="dfw1y50guc3h">
                  Exercise Your Faith
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-502" data-mp="2f8brik95xq70">
                  Since 2012, a ministry using outdoor adventure to strengthen
                  faith and build authentic community. Step away from the noise
                  and into creation to hear God's voice, face challenges with
                  resilience, and forge deep relationships. #PrayGoRepeat
                </p>
                <Link to="/ends-adventures" data-id="element-503" data-mp="1y6ee6z4h17hk">
                  <Button variant="outline" className="w-full" data-id="element-504" data-mp="1y7ftjfsisg53">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-dark-800" data-id="element-505" data-mp="1ag0jt1yeywjt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-506" data-mp="1ah24zz958zf9">
          <div className="text-center mb-16" data-id="element-507" data-mp="1ffdg0db6dfz2">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-508" data-mp="1fhpwsn575kaj">
              What We Believe
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-509" data-mp="2qv2272gluayt"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-510" data-mp="2vtmtgcdvpkb4">
            {[{
            title: 'Real Stories',
            desc: "No scripts, no pretense. Just honest conversations about life's actual struggles and victories.",
            icon: <Mic className="w-8 h-8 text-gold-500" data-id="element-511" data-mp="f9jhzrdkvja5" />
          }, {
            title: 'Living Faith',
            desc: "Faith isn't just for Sunday. We explore how to walk with Jesus in our work, families, and doubts.",
            icon: <Heart className="w-8 h-8 text-gold-500" data-id="element-512" data-mp="jlnktp21xci" />
          }, {
            title: 'Grace in Process',
            desc: 'We celebrate progress, not perfection. God meets us exactly where we are, messy parts included.',
            icon: <Play className="w-8 h-8 text-gold-500" data-id="element-513" data-mp="3io5ftc48ab5r" />
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} data-id="element-514" data-mp="1lvn32fm4b46h" data-mpi={index}>
                <Card className="p-8 h-full text-center bg-dark-600 border-dark-500" data-id="element-515" data-mp="1yzqx3gji406a">
                  <div className="w-16 h-16 mx-auto bg-dark-500 rounded-full flex items-center justify-center mb-6 border border-dark-400" data-id="element-516" data-mp="1u0xnhz7c4tbe">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-4" data-id="element-517" data-mp="1l095yuiqgatr">
                    {item.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed" data-id="element-518" data-mp="1g4abqqaq3ylf">{item.desc}</p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Listen On Section */}
      <section id="listen-platforms" className="py-20 bg-dark-800 border-t border-dark-600" data-id="element-519" data-mp="3adus8q1trucx">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-520" data-mp="3dyee3xvkwv4m">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-521" data-mp="3dw21m08lbcyy">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-522" data-mp="3rrdxiayhf9hg">
              Listen On Your Favorite Platform
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-523" data-mp="2z1ndxme2zfdy"></div>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-524" data-mp="33z733hcbhcko">
            {[{
            name: 'Spotify',
            url: 'https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL',
            color: '#1DB954',
            bg: 'bg-[#1DB954]/10',
            border: 'border-[#1DB954]/30',
            hoverBorder: 'hover:border-[#1DB954]/70'
          }, {
            name: 'Apple Podcasts',
            url: 'https://podcasts.apple.com/us/podcast/3-doves-podcast/id1871795398',
            color: '#B150E2',
            bg: 'bg-[#B150E2]/10',
            border: 'border-[#B150E2]/30',
            hoverBorder: 'hover:border-[#B150E2]/70'
          }, {
            name: 'Amazon Music',
            url: 'https://music.amazon.com/podcasts/1078f776-3803-4274-8e27-29857c6a295e/3-doves-podcast',
            color: '#00A8E1',
            bg: 'bg-[#00A8E1]/10',
            border: 'border-[#00A8E1]/30',
            hoverBorder: 'hover:border-[#00A8E1]/70'
          }, {
            name: 'iHeart Radio',
            url: 'https://www.iheart.com/podcast/269-3-doves-podcast-319099933/',
            color: '#C6002B',
            bg: 'bg-[#C6002B]/10',
            border: 'border-[#C6002B]/30',
            hoverBorder: 'hover:border-[#C6002B]/70'
          }].map((platform, index) => <motion.a key={platform.name} href={platform.url} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`flex flex-col items-center justify-center gap-4 p-6 rounded-xl border ${platform.bg} ${platform.border} ${platform.hoverBorder} transition-all duration-300 group`} data-id="element-525" data-mp="18iyd4f8w2asp" data-mpi={index}>
                <div className="w-14 h-14 flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-526" data-mp="24pebot3zyei">
                  {platform.name === 'Spotify' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#1DB954" data-id="element-527" data-mp="mcnihgpvghg3">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" data-id="element-528" data-mp="cmkqnjuhl9jc" />
                    </svg>}
                  {platform.name === 'Apple Podcasts' && <svg viewBox="0 0 50 50" className="w-12 h-12" fill="none" data-id="element-529" data-mp="3bzd281bouzyb">
                      <defs data-id="element-530" data-mp="1rjx3i097iuq1">
                        <linearGradient id="applePodGrad" x1="25" y1="2" x2="25" y2="48" gradientUnits="userSpaceOnUse" data-id="element-531" data-mp="1rgj3gyo0mxlr">
                          <stop stopColor="#F452FF" data-id="element-532" data-mp="25c3hpgssurmu" />
                          <stop offset="1" stopColor="#832BC1" data-id="element-533" data-mp="2a824x12e63lo" />
                        </linearGradient>
                      </defs>
                      <rect width="50" height="50" rx="11" fill="url(#applePodGrad)" data-id="element-534" data-mp="2f6dgbg8c4m60" />
                      <path d="M25 11.5a8.5 8.5 0 0 0-3.5 16.23V38a3.5 3.5 0 1 0 7 0V27.73A8.5 8.5 0 0 0 25 11.5Zm0 4a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z" fill="#fff" data-id="element-535" data-mp="2o6sbd7b3pfmp" />
                      <path d="M25 7C16.72 7 10 13.72 10 22c0 4.97 2.42 9.37 6.14 12.1a1.5 1.5 0 1 0 1.72-2.46A11.96 11.96 0 0 1 13 22c0-6.63 5.37-12 12-12s12 5.37 12 12a11.96 11.96 0 0 1-4.86 9.64 1.5 1.5 0 1 0 1.72 2.46C37.58 31.37 40 26.97 40 22c0-8.28-6.72-15-15-15Z" fill="#fff" data-id="element-536" data-mp="1x59bbr99hd6q" />
                    </svg>}
                  {platform.name === 'Amazon Music' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#00A8E1" data-id="element-537" data-mp="2g2ist2v10glc">
                      <path d="M14.43 17.59c-2.86.88-6.17 1.35-9.3.95-.52-.07-.61.39-.13.59 2.96 1.2 6.88 1.91 10.21 1.37.74-.12.87-.86.22-1.09-.65-.23-1-.82-1-.82zm1.51-1.14c-.11-.14-.28-.15-.42-.07-1.5.94-3.5 1.54-5.63 1.54-2.37 0-4.51-.68-6.1-1.81-.13-.09-.3-.08-.39.07-.09.15-.05.33.08.42 1.72 1.23 4.02 1.97 6.41 1.97 2.28 0 4.38-.63 5.98-1.7.13-.09.18-.27.07-.42zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.16 14.5c-2.02 1.5-4.76 2.38-7.66 2.38-3.53 0-6.71-1.31-9.13-3.47.48.48 1.04.89 1.66 1.22 1.72 1.23 4.02 1.97 6.41 1.97 2.28 0 4.38-.63 5.98-1.7 1.6-1.07 2.74-2.61 3.13-4.4.09-.41-.22-.78-.64-.78h-.01c-.31 0-.58.22-.64.52-.31 1.54-1.26 2.87-2.6 3.76z" data-id="element-538" data-mp="2ovnkxba8s3cl" />
                    </svg>}
                  {platform.name === 'iHeart Radio' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#C6002B" data-id="element-539" data-mp="2uqkad3jdt9ex">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" data-id="element-540" data-mp="2kto99ivtbpke" />
                    </svg>}
                </div>
                <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors text-center" data-id="element-541" data-mp="2buu8rhfd2f1k">
                  {platform.name}
                </span>
              </motion.a>)}
          </div>
        </div>
      </section>

      {/* Connect Form Section */}
      <section className="py-24 bg-dark-700 border-t border-dark-600" data-id="element-542" data-mp="2r8n4xsr7nenx">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-543" data-mp="2m9tq9x06thm6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-544" data-mp="2hd37v30wu9xl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-545" data-mp="23loogv33c6q0">
              Connect With Us
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-6" data-id="element-546" data-mp="1yjpn1qqwvjvp"></div>
            <p className="text-xl text-white/80" data-id="element-547" data-mp="1tlxtksambppe">
              Have a story to share? Want to be a guest? Drop us a message.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-dark-800 rounded-2xl p-6 md:p-10 border border-dark-600 shadow-xl" data-id="element-548" data-mp="53uycd0itbdl">
            <form onSubmit={onSubmit} className="space-y-6" data-id="element-549" data-mp="3hmbcj9e7f1zr">
              <div data-id="element-550" data-mp="3mitey2m2ao8x">
                <label htmlFor="name" className="block text-sm font-medium text-white/70 mb-2" data-id="element-551" data-mp="3mkcwrxnd97ct">
                  Name
                </label>
                <input type="text" name="name" id="name" required placeholder="Your name" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-552" data-mp="4s5rs1q8xmi" />
              </div>
              <div data-id="element-553" data-mp="2ig6gsurllrp4">
                <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2" data-id="element-554" data-mp="24ihvjltmcijj">
                  Email
                </label>
                <input type="email" name="email" id="email" required placeholder="your@email.com" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-555" data-mp="1usojykth83fa" />
              </div>
              <div data-id="element-556" data-mp="1bv5ajg33wisc">
                <label htmlFor="phone" className="block text-sm font-medium text-white/70 mb-2" data-id="element-557" data-mp="1bum34tb31csk">
                  Phone Number
                </label>
                <input type="tel" name="phone" id="phone" placeholder="(555) 123-4567" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-558" data-mp="x7dkx8jcw773" />
              </div>
              <div data-id="element-559" data-mp="1g3mxfrem0poz">
                <label htmlFor="message" className="block text-sm font-medium text-white/70 mb-2" data-id="element-560" data-mp="1b768iuzl67au">
                  Message
                </label>
                <textarea name="message" id="message" required rows={5} placeholder="Tell us your story, ask a question, or share how we can pray for you..." className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors resize-none" data-id="element-561" data-mp="1kwy4ntnc38hf" />
              </div>
              {/* Captcha Checkbox */}
              <div onClick={() => setCaptchaChecked(!captchaChecked)} className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200 select-none ${captchaChecked ? 'bg-dark-600 border-gold-500/50' : 'bg-dark-700 border-dark-500 hover:border-dark-400'}`} data-id="element-562" data-mp="10jd6xe3t97x7">
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${captchaChecked ? 'bg-gold-500 border-gold-500' : 'border-dark-400 bg-transparent'}`} data-id="element-563" data-mp="1k8x3nq6mipov">
                  {captchaChecked && <ShieldCheck className="w-4 h-4 text-dark-900" data-id="element-564" data-mp="3q1c6msqrv3lb" />}
                </div>
                <span className="text-white/80 text-sm" data-id="element-565" data-mp="3iimgqknke1ce">I'm not a robot</span>
                <input type="hidden" name="botcheck" value="" data-id="element-566" data-mp="3drcrvm9ub21k" />
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-567" data-mp="3dp9tadumg0l8">
                <Button type="submit" size="lg" leftIcon={<Send className="w-4 h-4" data-id="element-569" data-mp="34s996jel4onl" />} data-id="element-568" data-mp="38st37e3v3c5i">
                  Send Message
                </Button>
                {formResult && <span className={`text-sm font-medium flex items-center gap-2 ${formResult === 'Form Submitted Successfully' ? 'text-green-400' : formResult === 'Error' || formResult === 'Please verify you are not a robot.' ? 'text-red-400' : 'text-gold-400'}`} data-id="element-570" data-mp="cpynh81q3le">
                    {formResult === 'Form Submitted Successfully' && <CheckCircle className="w-4 h-4" data-id="element-571" data-mp="2gyr4d74gylqq" />}
                    {formResult}
                  </span>}
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-id="element-572" data-mp="8llyswlycyz6">
        <div className="absolute inset-0 bg-gold-600/10" data-id="element-573" data-mp="3qyk2nelv8bym"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-574" data-mp="3qvxo2v0k4w6w">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-575" data-mp="3v05agljjm01y">
            Support the Mission
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-576" data-mp="mhzd5wv6wk9f">
            Help us continue sharing these stories and providing essential bags
            to those in need through our outreach program.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-577" data-mp="ihp9wrys87y9">
            <Link to="/donate" data-id="element-578" data-mp="2y9curp0iua0p">
              <Button size="lg" data-id="element-579" data-mp="2y93dcptjlb6m">Give Now</Button>
            </Link>
            <Link to="/outreach" data-id="element-580" data-mp="338fzt9gi4g8o">
              <Button variant="outline" size="lg" data-id="element-581" data-mp="338ppsx1jqned">
                Learn About Outreach
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageTransition>;
}